'use client';
import Image from 'next/image';
import { useEffect, useId, useRef, useState } from 'react';
import {
  Image as BootstrapImage,
  Figure,
  FigureCaption,
} from 'react-bootstrap';
import { ChevronLeft, ChevronRight } from 'react-bootstrap-icons';
import './productImageGallery.scss';

const AUTOPLAY_MS = 5200;
const DRAG_THRESHOLD = 48;
const DEFAULT_IMAGE_DIMENSIONS = { width: 1200, height: 1200 };

function readMessage(messages, path, fallback = '') {
  const resolved = path
    .split('.')
    .reduce((current, segment) => current?.[segment], messages);

  return typeof resolved === 'string' ? resolved : fallback;
}

function fillTemplate(template, replacements = {}) {
  return Object.entries(replacements).reduce(
    (value, [key, replacement]) =>
      value.replaceAll(`{{${key}}}`, String(replacement)),
    String(template || ''),
  );
}

function normalizeGalleryImages(product) {
  const coverImages = Array.isArray(product?.coverImage)
    ? product.coverImage
    : [product?.coverImage];
  const secondaryImages = Array.isArray(product?.imagesPaths)
    ? product.imagesPaths
    : [];
  const seen = new Set();

  return [...coverImages, ...secondaryImages].filter((entry) => {
    const normalized = String(entry || '').trim();

    if (!normalized || seen.has(normalized)) {
      return false;
    }

    seen.add(normalized);
    return true;
  });
}

export default function ProductImageGallery({ messages = {}, product }) {
  const galleryId = useId();
  const images = normalizeGalleryImages(product);
  const thumbnailRailRef = useRef(null);
  const autoplayTimeoutRef = useRef(null);
  const thumbnailPointerStateRef = useRef({
    active: false,
    moved: false,
    pointerId: null,
    targetIndex: null,
    startScrollLeft: 0,
    startX: 0,
  });
  const pointerStateRef = useRef({
    active: false,
    moved: false,
    pointerId: null,
    startX: 0,
  });
  const [currentIndex, setCurrentIndex] = useState(0);
  const [initialImageLoaded, setInitialImageLoaded] = useState(false);
  const [overlayPhase, setOverlayPhase] = useState('full');
  const [imageOrientation, setImageOrientation] = useState('is-portrait');
  const [imageMetricsBySrc, setImageMetricsBySrc] = useState({});
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isInteractionPaused, setIsInteractionPaused] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isThumbnailDragging, setIsThumbnailDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [transitionDirection, setTransitionDirection] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const finalizeInitialOverlay = () => {
    setOverlayPhase('is-fadeout');
    window.setTimeout(() => setInitialImageLoaded(true), 420);
  };

  useEffect(() => {
    if (initialImageLoaded) {
      return undefined;
    }

    // Safety fallback: never leave the first-view overlay stuck forever.
    const timeoutId = window.setTimeout(() => {
      setOverlayPhase('is-prefade');
      finalizeInitialOverlay();
    }, 3200);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [initialImageLoaded]);

  useEffect(() => {
    setCurrentIndex(0);
    setInitialImageLoaded(false);
    setOverlayPhase('full');
    setImageOrientation('is-portrait');
    setIsInteractionPaused(false);
    setIsDragging(false);
    setIsThumbnailDragging(false);
    setDragOffset(0);
    setTransitionDirection(0);
    setIsTransitioning(false);
  }, [product?.productNumber]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateReducedMotion = () => {
      setPrefersReducedMotion(mediaQuery.matches);
    };

    updateReducedMotion();
    mediaQuery.addEventListener('change', updateReducedMotion);

    return () => {
      mediaQuery.removeEventListener('change', updateReducedMotion);
    };
  }, []);

  if (!images.length) {
    return (
      <div className="product-gallery product-gallery--empty">
        <div className="product-gallery__empty-copy">No image available</div>
      </div>
    );
  }

  const total = images.length;
  const currentImage = images[currentIndex] || images[0];
  const currentImageMetrics =
    imageMetricsBySrc[currentImage] || DEFAULT_IMAGE_DIMENSIONS;
  const galleryLabel = fillTemplate(
    readMessage(
      messages,
      'carouselAria',
      'Product images carousel for {{name}}',
    ),
    { name: product?.name || 'product' },
  );

  const moveToImage = (nextIndex, directionHint = 0) => {
    const normalizedIndex = (nextIndex + total) % total;

    if (normalizedIndex === currentIndex) {
      return;
    }

    const forwardIndex = (currentIndex + 1) % total;
    const backwardIndex = (currentIndex - 1 + total) % total;
    const nextDirection =
      directionHint ||
      (normalizedIndex === forwardIndex
        ? 1
        : normalizedIndex === backwardIndex
          ? -1
          : normalizedIndex > currentIndex
            ? 1
            : -1);

    setTransitionDirection(nextDirection);
    setIsTransitioning(!prefersReducedMotion);
    setCurrentIndex(normalizedIndex);
  };

  useEffect(() => {
    if (!isTransitioning || prefersReducedMotion) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setIsTransitioning(false);
      setTransitionDirection(0);
    }, 420);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [currentIndex, isTransitioning, prefersReducedMotion]);

  useEffect(() => {
    if (total <= 1) {
      return undefined;
    }

    const thumbnailRail = thumbnailRailRef.current;
    const activeThumbnail = thumbnailRail?.querySelector(
      `[data-gallery-thumb="${currentIndex}"]`,
    );

    if (
      !(activeThumbnail instanceof HTMLElement) ||
      !(thumbnailRail instanceof HTMLElement)
    ) {
      return undefined;
    }

    // Scroll only the thumbnail rail, not the page, to avoid unwanted page jumps.
    const thumbLeft = activeThumbnail.offsetLeft;
    const thumbWidth = activeThumbnail.offsetWidth;
    const railWidth = thumbnailRail.clientWidth;
    const targetScrollLeft = thumbLeft - railWidth / 2 + thumbWidth / 2;

    thumbnailRail.scrollTo({
      left: Math.max(0, targetScrollLeft),
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });

    return undefined;
  }, [currentIndex, prefersReducedMotion, total]);

  useEffect(() => {
    window.clearTimeout(autoplayTimeoutRef.current);

    if (
      total <= 1 ||
      prefersReducedMotion ||
      isInteractionPaused ||
      isDragging
    ) {
      return undefined;
    }

    autoplayTimeoutRef.current = window.setTimeout(() => {
      moveToImage(currentIndex + 1);
    }, AUTOPLAY_MS);

    return () => {
      window.clearTimeout(autoplayTimeoutRef.current);
    };
  }, [
    currentIndex,
    isDragging,
    isInteractionPaused,
    prefersReducedMotion,
    total,
  ]);

  const handleImageLoad = (event) => {
    const image = event.currentTarget;
    const width = image.naturalWidth || image.width;
    const height = image.naturalHeight || image.height;

    setImageMetricsBySrc((current) => {
      if (
        current[currentImage]?.width === width &&
        current[currentImage]?.height === height
      ) {
        return current;
      }

      return {
        ...current,
        [currentImage]: { width, height },
      };
    });

    setImageOrientation(height > width ? 'is-portrait' : 'is-landscape');

    if (!initialImageLoaded) {
      setOverlayPhase('is-prefade');

      try {
        const decodePromise = image.decode?.();

        if (decodePromise && typeof decodePromise.then === 'function') {
          decodePromise
            .then(finalizeInitialOverlay)
            .catch(finalizeInitialOverlay);
        } else {
          finalizeInitialOverlay();
        }
      } catch {
        finalizeInitialOverlay();
      }
    }
  };

  const handleImageError = () => {
    // Do not keep the blur/skeleton overlay forever if an early image fails.
    if (!initialImageLoaded) {
      setOverlayPhase('is-prefade');
      finalizeInitialOverlay();
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      moveToImage(currentIndex - 1, -1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      moveToImage(currentIndex + 1, 1);
    }

    if (event.key === 'Home') {
      event.preventDefault();
      moveToImage(0, -1);
    }

    if (event.key === 'End') {
      event.preventDefault();
      moveToImage(total - 1, 1);
    }
  };

  const resetDrag = () => {
    pointerStateRef.current.active = false;
    pointerStateRef.current.moved = false;
    pointerStateRef.current.pointerId = null;
    setIsDragging(false);
    setDragOffset(0);
  };

  const handlePointerDown = (event) => {
    if (total <= 1) {
      return;
    }

    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    pointerStateRef.current.active = true;
    pointerStateRef.current.moved = false;
    pointerStateRef.current.pointerId = event.pointerId;
    pointerStateRef.current.startX = event.clientX;
    setIsDragging(true);
    setIsInteractionPaused(true);

    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {}
  };

  const handlePointerMove = (event) => {
    if (!pointerStateRef.current.active) {
      return;
    }

    const deltaX = event.clientX - pointerStateRef.current.startX;

    if (Math.abs(deltaX) > 6) {
      pointerStateRef.current.moved = true;
    }

    setDragOffset(Math.max(-120, Math.min(120, deltaX)));
  };

  const handlePointerRelease = (event) => {
    if (!pointerStateRef.current.active) {
      return;
    }

    const deltaX = event.clientX - pointerStateRef.current.startX;

    try {
      if (pointerStateRef.current.pointerId !== null) {
        event.currentTarget.releasePointerCapture(
          pointerStateRef.current.pointerId,
        );
      }
    } catch {}

    resetDrag();
    setIsInteractionPaused(false);

    if (Math.abs(deltaX) < DRAG_THRESHOLD) {
      return;
    }

    moveToImage(currentIndex + (deltaX < 0 ? 1 : -1), deltaX < 0 ? 1 : -1);
  };

  const handleThumbnailPointerDown = (event) => {
    const rail = thumbnailRailRef.current;

    if (!(rail instanceof HTMLElement) || total <= 1) {
      return;
    }

    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    thumbnailPointerStateRef.current.active = true;
    thumbnailPointerStateRef.current.moved = false;
    thumbnailPointerStateRef.current.pointerId = event.pointerId;
    thumbnailPointerStateRef.current.targetIndex =
      event.target instanceof Element
        ? Number(
            event.target
              .closest('[data-gallery-thumb]')
              ?.getAttribute('data-gallery-thumb'),
          )
        : null;
    thumbnailPointerStateRef.current.startScrollLeft = rail.scrollLeft;
    thumbnailPointerStateRef.current.startX = event.clientX;
    setIsThumbnailDragging(true);
    setIsInteractionPaused(true);

    try {
      rail.setPointerCapture(event.pointerId);
    } catch {}
  };

  const handleThumbnailPointerMove = (event) => {
    const rail = thumbnailRailRef.current;

    if (
      !(rail instanceof HTMLElement) ||
      !thumbnailPointerStateRef.current.active
    ) {
      return;
    }

    const deltaX = event.clientX - thumbnailPointerStateRef.current.startX;

    if (Math.abs(deltaX) > 6) {
      thumbnailPointerStateRef.current.moved = true;
    }

    rail.scrollLeft = thumbnailPointerStateRef.current.startScrollLeft - deltaX;
  };

  const handleThumbnailPointerRelease = (event) => {
    const rail = thumbnailRailRef.current;

    if (
      !(rail instanceof HTMLElement) ||
      !thumbnailPointerStateRef.current.active
    ) {
      return;
    }

    try {
      if (thumbnailPointerStateRef.current.pointerId !== null) {
        rail.releasePointerCapture(thumbnailPointerStateRef.current.pointerId);
      }
    } catch {}

    const nextIndex = thumbnailPointerStateRef.current.targetIndex;
    const moved = thumbnailPointerStateRef.current.moved;
    const deltaX = event.clientX - thumbnailPointerStateRef.current.startX;

    thumbnailPointerStateRef.current.active = false;
    thumbnailPointerStateRef.current.pointerId = null;
    thumbnailPointerStateRef.current.targetIndex = null;
    thumbnailPointerStateRef.current.moved = false;
    setIsThumbnailDragging(false);
    setIsInteractionPaused(false);

    if (
      !moved &&
      Number.isInteger(nextIndex) &&
      Math.abs(deltaX) < DRAG_THRESHOLD
    ) {
      moveToImage(nextIndex, nextIndex > currentIndex ? 1 : -1);
    }
  };

  return (
    <section
      className="product-gallery"
      aria-label={galleryLabel}
      aria-roledescription={total > 1 ? 'carousel' : undefined}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setIsInteractionPaused(true)}
      onMouseLeave={() => setIsInteractionPaused(false)}
      onFocusCapture={() => setIsInteractionPaused(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) {
          setIsInteractionPaused(false);
        }
      }}
      tabIndex={0}
    >
      <div className="visually-hidden" aria-live="polite">
        {fillTemplate(
          readMessage(
            messages,
            'carousel.announce',
            'Showing image {{current}} of {{total}}',
          ),
          { current: currentIndex + 1, total },
        )}
      </div>

      <div className="product-gallery__stage">
        <div
          className={`product-gallery__frame${isDragging ? ' is-dragging' : ''}`}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerRelease}
          onPointerCancel={handlePointerRelease}
        >
          {!initialImageLoaded ? (
            <div
              className={`product-gallery__overlay is-active ${overlayPhase}`.trim()}
              aria-hidden="true"
            />
          ) : null}

          <Figure id={galleryId} className="product-gallery__media">
            <div
              className="product-gallery__media-inner"
              style={{ transform: `translate3d(${dragOffset}px, 0, 0)` }}
            >
              <div
                className={`product-gallery__slide${
                  isTransitioning ? ' is-transitioning' : ' is-entered'
                }`}
                data-direction={transitionDirection}
              >
                <BootstrapImage
                  as={Image}
                  src={currentImage}
                  alt={fillTemplate(
                    readMessage(messages, 'carousel.alt', 'Product image'),
                    {
                      index: currentIndex + 1,
                      total,
                      name: product?.name || 'product',
                    },
                  )}
                  className={`product-gallery__image ${imageOrientation} ${
                    !initialImageLoaded ? 'is-loading' : ''
                  }`.trim()}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                  draggable={false}
                  width={currentImageMetrics.width}
                  height={currentImageMetrics.height}
                  loading={currentIndex === 0 ? 'eager' : 'lazy'}
                  sizes="(max-width: 849px) 100vw, (max-width: 1200px) 58vw, 44vw"
                />
              </div>
            </div>

            {/* {total > 1 ? (
              <FigureCaption className="product-gallery__status">
                {fillTemplate(
                  readMessage(
                    messages,
                    'carouselStatus',
                    'Image {{current}} / {{total}}',
                  ),
                  { current: currentIndex + 1, total },
                )}
              </FigureCaption>
            ) : null} */}
          </Figure>
        </div>
      </div>

      {total > 1 ? (
        <div className="product-gallery__thumbs-shell">
          <button
            type="button"
            className="product-gallery__thumb-nav product-gallery__thumb-nav--prev"
            onClick={() => moveToImage(currentIndex - 1, -1)}
            aria-label={readMessage(
              messages,
              'carousel.prev',
              'Previous image',
            )}
          >
            <ChevronLeft size={16} aria-hidden="true" />
          </button>

          <div
            ref={thumbnailRailRef}
            className={`product-gallery__thumbnails${
              isThumbnailDragging ? ' is-dragging' : ''
            }`}
            aria-label={readMessage(
              messages,
              'carouselDots',
              'Image indicators',
            )}
            onPointerDown={handleThumbnailPointerDown}
            onPointerMove={handleThumbnailPointerMove}
            onPointerUp={handleThumbnailPointerRelease}
            onPointerCancel={handleThumbnailPointerRelease}
          >
            {images.map((image, index) => (
              <button
                key={`${image}-${index}`}
                type="button"
                data-gallery-thumb={index}
                aria-pressed={index === currentIndex}
                aria-label={fillTemplate(
                  readMessage(
                    messages,
                    'carousel.slideLabel',
                    'Image {{index}} of {{total}}',
                  ),
                  { index: index + 1, total },
                )}
                className={`product-gallery__thumbnail${
                  index === currentIndex ? ' is-active' : ''
                }`}
                onClick={(event) => {
                  if (event.detail === 0) {
                    moveToImage(index, index > currentIndex ? 1 : -1);
                  }
                }}
              >
                <span className="product-gallery__thumbnail-media">
                  <Image
                    src={image}
                    alt=""
                    className="product-gallery__thumbnail-image"
                    draggable={false}
                    fill
                    sizes="76px"
                  />
                </span>
              </button>
            ))}
          </div>

          <button
            type="button"
            className="product-gallery__thumb-nav product-gallery__thumb-nav--next"
            onClick={() => moveToImage(currentIndex + 1, 1)}
            aria-label={readMessage(messages, 'carousel.next', 'Next image')}
          >
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
      ) : null}
    </section>
  );
}
